import styled, { css, keyframes } from 'styled-components';

import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

type SpinnerProps = {
  className?: string;
};

export const Spinner = ({ className }: SpinnerProps) => (
  <StyledLoading viewBox="0 0 50 50" className={className}>
    <circle cx="25" cy="25" r="20" fill="none" strokeWidth="6" strokeLinecap="round" />
  </StyledLoading>
);

const rotate = keyframes`
  100% {
    transform: rotate(360deg);
  }
`;

const dash = keyframes`
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
`;

const rotateRule = css`
  ${rotate} 2s linear infinite
`;

const dashRule = css`
  ${dash} 1.5s ease-in-out infinite
`;

const StyledLoading = styled.svg`
  z-index: 1000;
  animation: ${rotateRule};
  position: fixed;
  top: 50%;
  left: 50%;
  margin: -1.5rem 0 0 -1.5rem;
  width: 3rem;
  height: 3rem;
  display: block;
  circle {
    stroke: ${theme.color.uiTrans50};
    animation: ${dashRule};
  }
`;

export const RelativeSpinner = styled(Spinner)`
  && {
    position: relative;
    top: auto;
    left: auto;
    margin: auto;
  }
`;

export const SizedRelativeSpinner = styled(Spinner)<{
  size: number;
  $skipMargin?: boolean;
}>(({ size, $skipMargin }) => {
  // When the spinner rotates, we need a margin at the bottom that is equal to the diagonal to the center of the square
  const marginBottom = Math.sqrt(Math.pow(size, 2) / 2) - size / 2;
  return {
    '&&': {
      position: 'relative',
      overflow: 'hidden',
      top: 'auto',
      left: 'auto',
      margin: $skipMargin ? '0' : `auto auto ${pxToRem(marginBottom)} auto`,
      width: pxToRem(size),
      height: pxToRem(size),
      display: 'block',
    },
  };
});
